import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Settings2, ShieldAlert, ShieldCheck, Plus, Trash2, RefreshCw, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PROFILE_BY_LEVEL, VAR_CEILING } from "@/lib/risk-policy";
import { format } from "date-fns";

interface Props { range: 'P' | 'R' }

interface Scenario { id: string; name: string; start_date: string; end_date: string; equity_dd_pct: number; credit_dd_pct: number; notes: string | null }
interface IaSector { id: string; name: string; sort_order: number }
interface FundIaMap { id?: string; fund_id: string; ia_sector_id: string; weighting: number }
interface Fund { id: string; fe_fund_name: string; fund_name: string }
interface PortfolioStress {
  scenario_id: string;
  portfolio_range: 'P' | 'R';
  portfolio_level: number;
  peak_date: string | null;
  trough_date: string | null;
  stressed_loss_pct: number | null;
  ia_breakdown: Record<string, { exposure: number; ia_loss_pct: number; contribution_pct: number }> | null;
  unmapped_weight_pct: number | null;
  computed_at: string;
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  try { return format(new Date(d), 'dd/MM/yyyy'); } catch { return d; }
}

interface DailyValue { portfolio_level: number; series_type: 'portfolio' | 'benchmark'; value_date: string; value: number }

function computeVaR(values: number[], horizonDays: number, confidence: number): number | null {
  const rets: number[] = [];
  for (let i = 1; i < values.length; i++) {
    const a = values[i - 1], b = values[i];
    if (a > 0 && b > 0) rets.push(Math.log(b / a));
  }
  if (rets.length < horizonDays) return null;
  const horizonReturns: number[] = [];
  for (let i = horizonDays; i <= rets.length; i++) {
    let s = 0;
    for (let j = i - horizonDays; j < i; j++) s += rets[j];
    horizonReturns.push(Math.expm1(s));
  }
  if (!horizonReturns.length) return null;
  horizonReturns.sort((a, b) => a - b);
  const idx = Math.floor((1 - confidence) * horizonReturns.length);
  return Math.max(0, -horizonReturns[Math.max(0, idx)]) * 100;
}

async function fetchAllDaily(range: 'P' | 'R'): Promise<DailyValue[]> {
  const all: DailyValue[] = [];
  const pageSize = 1000;
  let from = 0;
  while (true) {
    const { data, error } = await supabase
      .from('risk_daily_values')
      .select('portfolio_level, series_type, value_date, value')
      .eq('portfolio_range', range)
      .eq('series_type', 'portfolio')
      .order('value_date', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...(data as DailyValue[]));
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

export default function StressReplayPanel({ range }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  const { data: scenarios = [] } = useQuery({
    queryKey: ['risk-scenarios'],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_stress_scenarios').select('*').order('start_date');
      if (error) throw error;
      return data as Scenario[];
    },
  });

  const { data: iaSectors = [] } = useQuery({
    queryKey: ['ia-sectors'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ia_sectors').select('id, name, sort_order').order('sort_order');
      if (error) throw error;
      return data as IaSector[];
    },
  });

  const { data: portfolioStress = [] } = useQuery({
    queryKey: ['risk-scenario-portfolio-stress', range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('risk_scenario_portfolio_stress' as any)
        .select('*')
        .eq('portfolio_range', range);
      if (error) throw error;
      return (data ?? []) as unknown as PortfolioStress[];
    },
  });

  const { data: fundIaMaps = [] } = useQuery({
    queryKey: ['ba-cbam-fund-ia-sector-mappings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ba_cbam_fund_ia_sector_mappings').select('id, fund_id, ia_sector_id, weighting');
      if (error) throw error;
      return data as FundIaMap[];
    },
  });

  const { data: funds = [] } = useQuery({
    queryKey: ['ba-cbam-funds-min'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ba_cbam_funds').select('id, fe_fund_name, fund_name').order('fe_fund_name');
      if (error) throw error;
      return data as Fund[];
    },
  });

  const { data: settings } = useQuery({
    queryKey: ['risk-stress-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('risk_stress_settings').select('*').single();
      if (error) throw error;
      return data as { materially_exceeded_multiplier: number };
    },
  });

  const { data: dailyValues = [] } = useQuery({
    queryKey: ['risk-daily', range],
    queryFn: () => fetchAllDaily(range),
  });

  const activeScenario = scenarios.find(s => s.id === selectedScenarioId) ?? scenarios[0];
  const multiplier = settings?.materially_exceeded_multiplier ?? 1.5;

  const iaNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const ia of iaSectors) m.set(ia.id, ia.name);
    return m;
  }, [iaSectors]);

  const stressByLevel = useMemo(() => {
    const m = new Map<number, PortfolioStress>();
    if (!activeScenario) return m;
    for (const s of portfolioStress) {
      if (s.scenario_id === activeScenario.id) m.set(s.portfolio_level, s);
    }
    return m;
  }, [portfolioStress, activeScenario]);

  const varByLevel = useMemo(() => {
    const m = new Map<number, { v95: number | null; v99: number | null }>();
    for (let level = 1; level <= 5; level++) {
      const series = dailyValues
        .filter(d => d.portfolio_level === level)
        .map(d => Number(d.value));
      m.set(level, {
        v95: computeVaR(series, 365, 0.95),
        v99: computeVaR(series, 365, 0.99),
      });
    }
    return m;
  }, [dailyValues]);

  const recalc = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('recalc-stress-drawdowns');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Recalculated', description: 'Stress test results refreshed' });
      qc.invalidateQueries({ queryKey: ['risk-scenario-portfolio-stress'] });
    },
    onError: (e: any) => toast({ title: 'Recalc failed', description: e.message, variant: 'destructive' }),
  });

  const rows = [1, 2, 3, 4, 5].map(level => {
    const profile = PROFILE_BY_LEVEL[level];
    const ceiling = VAR_CEILING[profile];
    const s = stressByLevel.get(level);
    const stressed = s?.stressed_loss_pct ?? null;
    const threshold = ceiling * multiplier;
    const status: 'green' | 'amber' | 'red' | 'none' = stressed == null
      ? 'none'
      : stressed > threshold ? 'red'
      : stressed > ceiling ? 'amber' : 'green';
    const v = varByLevel.get(level);
    return { level, profile, ceiling, threshold, stressed, status, stress: s, var95: v?.v95 ?? null, var99: v?.v99 ?? null };
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Stress Replay — {range} Portfolios</CardTitle>
            <CardDescription>
              Tier 1 §11.5 quarterly crisis replay. Stressed loss = portfolio peak-to-trough inside the crisis window, computed daily from each portfolio's IA-sector exposure × IA index series.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => recalc.mutate()} disabled={recalc.isPending}>
              <RefreshCw className={`h-4 w-4 mr-2 ${recalc.isPending ? 'animate-spin' : ''}`} />
              {recalc.isPending ? 'Recalculating…' : 'Recalculate from sheet'}
            </Button>
            <SettingsDialog
              onSaved={() => qc.invalidateQueries()}
              iaSectors={iaSectors}
              scenarios={scenarios}
              settings={settings}
              funds={funds}
              fundIaMaps={fundIaMaps}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Crisis window</Label>
              <Select value={activeScenario?.id ?? ''} onValueChange={setSelectedScenarioId}>
                <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select scenario" /></SelectTrigger>
                <SelectContent>
                  {scenarios.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeScenario && (
              <div className="text-xs text-muted-foreground">
                Window: {fmtDate(activeScenario.start_date)} → {fmtDate(activeScenario.end_date)}
              </div>
            )}
            <div className="ml-auto text-xs text-muted-foreground">
              Materially-exceeded multiplier: <span className="font-medium text-foreground">{multiplier}×</span>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Portfolio</TableHead>
                <TableHead>Risk Profile</TableHead>
                <TableHead>Peak</TableHead>
                <TableHead>Trough</TableHead>
                <TableHead className="text-right">Stressed Loss (%)</TableHead>
                <TableHead className="text-right">95% VaR (%)</TableHead>
                <TableHead className="text-right">99% VaR (%)</TableHead>
                <TableHead className="text-right">VaR Ceiling (%)</TableHead>
                <TableHead className="text-right">Red Threshold</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Unmapped wt.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <>
                  <TableRow key={r.level}>
                    <TableCell>
                      {r.stress?.ia_breakdown && (
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setExpandedLevel(expandedLevel === r.level ? null : r.level)}>
                          <ChevronRight className={`h-4 w-4 transition-transform ${expandedLevel === r.level ? 'rotate-90' : ''}`} />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{range}{r.level}</TableCell>
                    <TableCell>{r.profile}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(r.stress?.peak_date ?? null)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(r.stress?.trough_date ?? null)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.stressed != null ? r.stressed.toFixed(2) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.var95 != null ? r.var95.toFixed(2) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.var99 != null ? r.var99.toFixed(2) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.ceiling.toFixed(1)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.threshold.toFixed(2)}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {r.stress?.unmapped_weight_pct != null ? `${r.stress.unmapped_weight_pct.toFixed(1)}%` : '—'}
                    </TableCell>
                  </TableRow>
                  {expandedLevel === r.level && r.stress?.ia_breakdown && (
                    <TableRow key={`${r.level}-detail`}>
                      <TableCell colSpan={12} className="bg-muted/30 p-0">
                        <div className="p-3">
                          <p className="text-xs text-muted-foreground mb-2">
                            IA-sector contributions between {range}{r.level}'s peak ({fmtDate(r.stress.peak_date)}) and trough ({fmtDate(r.stress.trough_date)}). Contribution = exposure × IA loss between those two dates.
                          </p>
                          <div className="max-h-80 overflow-y-auto border rounded bg-background">
                            <Table>
                              <TableHeader className="sticky top-0 bg-background">
                                <TableRow>
                                  <TableHead>IA Sector</TableHead>
                                  <TableHead className="text-right">Exposure %</TableHead>
                                  <TableHead className="text-right">IA Loss %</TableHead>
                                  <TableHead className="text-right">Contribution %</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {Object.entries(r.stress.ia_breakdown)
                                  .filter(([, b]) => b.exposure > 0.001)
                                  .sort((a, b) => b[1].contribution_pct - a[1].contribution_pct)
                                  .map(([sid, b]) => (
                                    <TableRow key={sid}>
                                      <TableCell className="text-xs">{iaNameById.get(sid) ?? sid}</TableCell>
                                      <TableCell className="text-right tabular-nums text-xs">{b.exposure.toFixed(2)}</TableCell>
                                      <TableCell className="text-right tabular-nums text-xs">{b.ia_loss_pct.toFixed(2)}</TableCell>
                                      <TableCell className={`text-right tabular-nums text-xs ${b.contribution_pct > 0 ? '' : 'text-muted-foreground'}`}>
                                        {b.contribution_pct.toFixed(3)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground mt-3">
            Peak/trough dates are portfolio-specific — found by walking the daily portfolio NAV inside the crisis window. IA contributions are measured between that portfolio's own peak and trough dates, so they sum to the headline loss (less rounding).
          </p>
          {rows.every(r => r.stressed == null) && (
            <p className="text-xs text-amber-600 mt-2">
              No computed stress yet. Make sure fund → IA-sector mappings are populated in Settings, then click <strong>Recalculate from sheet</strong>.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Policy reference — Tier 1 §11.5</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Quarterly stress replay over historical crisis windows. Stressed loss is the portfolio's peak-to-trough fall inside the window, compared against the §11.2 VaR ceiling. A portfolio is flagged red when the stressed loss <strong>materially exceeds</strong> the ceiling — defined here as &gt; multiplier × ceiling (default 1.5×, configurable).</p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: 'green' | 'amber' | 'red' | 'none' }) {
  if (status === 'red') return <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />Materially exceeded</Badge>;
  if (status === 'amber') return <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-600"><ShieldAlert className="h-3 w-3" />Above ceiling</Badge>;
  if (status === 'green') return <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Within ceiling</Badge>;
  return <Badge variant="outline">No data</Badge>;
}

function SettingsDialog({
  iaSectors, scenarios, settings, funds, fundIaMaps, onSaved,
}: {
  iaSectors: IaSector[];
  scenarios: Scenario[];
  settings: { materially_exceeded_multiplier: number } | undefined;
  funds: Fund[];
  fundIaMaps: FundIaMap[];
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [localMultiplier, setLocalMultiplier] = useState<string>('1.5');
  const [localScenarios, setLocalScenarios] = useState<Scenario[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [fundMappings, setFundMappings] = useState<{ ia_sector_id: string; weighting: string }[]>([]);

  const init = () => {
    setLocalMultiplier(String(settings?.materially_exceeded_multiplier ?? 1.5));
    setLocalScenarios(scenarios.map(s => ({ ...s })));
    setSelectedFundId('');
    setFundMappings([]);
  };

  const loadFundMappings = (fundId: string) => {
    setSelectedFundId(fundId);
    const existing = fundIaMaps.filter(m => m.fund_id === fundId);
    setFundMappings(existing.map(e => ({ ia_sector_id: e.ia_sector_id, weighting: String(e.weighting) })));
  };

  const save = useMutation({
    mutationFn: async () => {
      const mult = Number(localMultiplier);
      if (!Number.isFinite(mult) || mult <= 0) throw new Error('Multiplier must be > 0');
      const { error: e2 } = await supabase.from('risk_stress_settings').update({ materially_exceeded_multiplier: mult, updated_at: new Date().toISOString() }).eq('id', true);
      if (e2) throw e2;
      for (const s of localScenarios) {
        const { error } = await supabase.from('risk_stress_scenarios').update({
          start_date: s.start_date,
          end_date: s.end_date,
        }).eq('id', s.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: 'Saved', description: 'Settings updated. Click Recalculate to re-run stress.' });
      onSaved();
    },
    onError: (e: any) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  const saveFundMappings = useMutation({
    mutationFn: async () => {
      if (!selectedFundId) throw new Error('Select a fund first');
      const { error: delErr } = await supabase.from('ba_cbam_fund_ia_sector_mappings').delete().eq('fund_id', selectedFundId);
      if (delErr) throw delErr;
      const rows = fundMappings
        .filter(m => m.ia_sector_id && Number(m.weighting) > 0)
        .map(m => ({ fund_id: selectedFundId, ia_sector_id: m.ia_sector_id, weighting: Number(m.weighting) }));
      if (rows.length) {
        const { error: insErr } = await supabase.from('ba_cbam_fund_ia_sector_mappings').insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      toast({ title: 'Saved', description: 'Fund IA mappings updated. Click Recalculate to re-run stress.' });
      onSaved();
    },
    onError: (e: any) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"><Settings2 className="h-4 w-4 mr-2" />Settings</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Stress Replay Settings</DialogTitle></DialogHeader>
        <Tabs defaultValue="general" className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">General & windows</TabsTrigger>
            <TabsTrigger value="mappings">Fund → IA mappings</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6">
            <section>
              <h3 className="font-medium mb-2">Materially-exceeded multiplier</h3>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.1" min={1} value={localMultiplier} onChange={e => setLocalMultiplier(e.target.value)} className="w-32" />
                <span className="text-sm text-muted-foreground">× VaR ceiling triggers red flag</span>
              </div>
            </section>

            <section>
              <h3 className="font-medium mb-2">Crisis window dates</h3>
              <div className="space-y-2">
                {localScenarios.map((s, idx) => (
                  <div key={s.id} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6"><Label className="text-xs">{s.name}</Label></div>
                    <div className="col-span-3"><Label className="text-xs">Start</Label><Input type="date" value={s.start_date} onChange={e => { const c=[...localScenarios]; c[idx].start_date=e.target.value; setLocalScenarios(c); }} /></div>
                    <div className="col-span-3"><Label className="text-xs">End</Label><Input type="date" value={s.end_date} onChange={e => { const c=[...localScenarios]; c[idx].end_date=e.target.value; setLocalScenarios(c); }} /></div>
                  </div>
                ))}
              </div>
            </section>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="mappings" className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Attach each fund to one or more IA performance sectors. Weightings should sum to 1.0 per fund (they will be normalised at calculation time if not). After saving, click <strong>Recalculate from sheet</strong> on the panel to re-run stress.
            </p>
            <div className="flex items-end gap-3">
              <div className="space-y-1.5 flex-1">
                <Label className="text-xs">Fund</Label>
                <Select value={selectedFundId} onValueChange={loadFundMappings}>
                  <SelectTrigger><SelectValue placeholder="Select fund" /></SelectTrigger>
                  <SelectContent>
                    {funds.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.fe_fund_name || f.fund_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={() => setFundMappings(prev => [...prev, { ia_sector_id: '', weighting: '1.0' }])} disabled={!selectedFundId}>
                <Plus className="h-4 w-4 mr-1" /> Add row
              </Button>
            </div>

            {selectedFundId && (
              <div className="border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>IA Sector</TableHead>
                      <TableHead className="w-32 text-right">Weighting</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fundMappings.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground text-sm py-4">No mappings yet</TableCell></TableRow>
                    )}
                    {fundMappings.map((m, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Select value={m.ia_sector_id} onValueChange={v => { const c = [...fundMappings]; c[idx].ia_sector_id = v; setFundMappings(c); }}>
                            <SelectTrigger><SelectValue placeholder="Select IA sector" /></SelectTrigger>
                            <SelectContent>
                              {iaSectors.map(ia => (
                                <SelectItem key={ia.id} value={ia.id}>{ia.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" min={0} max={1} value={m.weighting}
                            onChange={e => { const c = [...fundMappings]; c[idx].weighting = e.target.value; setFundMappings(c); }}
                            className="h-8 text-right tabular-nums" />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => setFundMappings(prev => prev.filter((_, i) => i !== idx))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-2 text-xs text-muted-foreground border-t">
                  <span>Sum: <span className="font-medium tabular-nums text-foreground">{fundMappings.reduce((a, m) => a + (Number(m.weighting) || 0), 0).toFixed(2)}</span></span>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={() => saveFundMappings.mutate()} disabled={saveFundMappings.isPending || !selectedFundId}>
                {saveFundMappings.isPending ? 'Saving…' : 'Save fund mappings'}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
